import type { ProductImageProviderProps } from './types';
import type { FC, PropsWithChildren } from 'react';

import { useMemo } from 'react';

import { IMAGE_TYPE } from './constants';
import { TileImageProvider } from './TileImageContext';
import { buildImageSource, resolveImageProfiles } from './utilities';
import type {
  HoverImageType,
  PrimaryImageType,
} from '~/amplienceTypes/schemas/imported/product-recommendations-schema';
import { useTileContext } from '~/components/shared/recommendations/tile/TileContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { findProfileImage } from '~/utilities/helpers/findImageProfile';

export const AAFProductImageProvider: FC<PropsWithChildren<ProductImageProviderProps>> = ({
  children,
  product,
}) => {
  const {
    configuration: { allowedProductTilePrimaryImageTypes, allowedProductTileHoverImageTypes },
  } = useStaticContext();
  const { baseImageUrl, aspectRatio, primaryImageType, hoverImageType } = useTileContext();

  const value = useMemo(() => {
    const findImageByProfiles = findProfileImage(
      new Map(Object.entries(product?.simplifiedImageJson ?? {}))
    );

    // On AAF the default primary shot is already the flat product image, so
    // `flat` resolves to the same profiles as `default primary`.
    const resolveProfiles = (imageType: PrimaryImageType | HoverImageType) =>
      resolveImageProfiles({
        imageType,
        primaryProfiles: allowedProductTilePrimaryImageTypes,
        hoverProfiles: allowedProductTileHoverImageTypes,
        flatProfiles: allowedProductTilePrimaryImageTypes,
      });

    const mainProfiles = resolveProfiles(primaryImageType ?? IMAGE_TYPE.defaultPrimary);
    const hoverProfiles = resolveProfiles(hoverImageType ?? IMAGE_TYPE.defaultHover);
    // When both views resolve to the same image, there is nothing to swap to on hover
    const hoverDisabled = hoverProfiles === mainProfiles;

    const getHoverImage = () => (hoverDisabled ? undefined : findImageByProfiles(hoverProfiles));
    const getPrimaryImage = () => findImageByProfiles(mainProfiles);

    const primaryImage = getPrimaryImage();
    const hoverImage = getHoverImage();

    return {
      primaryImage: buildImageSource(
        primaryImage?.url,
        baseImageUrl,
        aspectRatio,
        primaryImage?.isAI
      ),
      hoverImage: buildImageSource(hoverImage?.url, baseImageUrl, aspectRatio, hoverImage?.isAI),
      aspectRatio,
    };
  }, [
    allowedProductTileHoverImageTypes,
    allowedProductTilePrimaryImageTypes,
    baseImageUrl,
    aspectRatio,
    product,
    primaryImageType,
    hoverImageType,
  ]);

  return <TileImageProvider value={value}>{children}</TileImageProvider>;
};
